// src/components/ws/WsOverview.jsx
// 出席記録（全画面）の「概要」タブ【2026-10-13】
//
//   開いて最初に見る画面。1画面に収める。
//     左：回ごとの出席率の棒グラフ（押すとその回を「回ごと」タブで開く）と、のべの数字
//     右：片付けが必要なこと（同じ日に回が2つ・名簿にいない学生・申請／確認待ち）と、要注意の学生
//   数字の「誰が当てはまるか」は、もとの出席記録と同じ StudentList を出す。
import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { StudentList, rowsForOverview, splitStudents, OutsiderPanel } from '../SummaryDrill';
import { fmtDate, fmtMD, fmtClock, rateColor, byDateAsc } from './wsCommon';

export default function WsOverview({ classId, data, matrix, risk, todo, goTab, auth, setErr, setMsg, onChanged }) {
  const navigate = useNavigate();
  const [drill, setDrill] = useState(null);           // { kind, title }
  const [showOutsiders, setShowOutsiders] = useState(false);

  const ov = (data && data.overview) || {};
  const enrolled = Number(ov.enrolled_students || 0);
  const held = Number(ov.total_sessions || 0) - Number(ov.cancelled_sessions || 0);

  // 回ごとの出席率（古い順・休講を除く）
  const bars = useMemo(() => {
    const list = ((data && data.sessions) || []).filter(s => s.status !== 'cancelled').slice().sort(byDateAsc);
    return list.map((s, i) => {
      const att = Number(s.attended_count || 0);
      const rate = enrolled > 0 ? Math.round(att * 1000 / enrolled) / 10 : null;
      return { s, i, rate, att };
    });
  }, [data, enrolled]);

  // 同じ日に回が2つ以上（第何回がずれる原因）
  const dups = useMemo(() => {
    const byDay = {};
    ((data && data.sessions) || []).filter(s => s.status !== 'cancelled').forEach(s => {
      const k = s.session_ymd || fmtDate(s.session_date);
      (byDay[k] = byDay[k] || []).push(s);
    });
    return Object.entries(byDay).filter(([, l]) => l.length >= 2).sort(([a], [b]) => (a < b ? 1 : -1));
  }, [data]);

  const split = useMemo(() => splitStudents(matrix), [matrix]);
  const outsiders = split.outsiders;
  const placeholders = split.enrolled.filter(s => s.is_placeholder);
  const excusedTotal = split.enrolled.reduce((a, s) => a + Object.values(s.cells || {}).filter(x => x === 'excused').length, 0);
  const riskList = useMemo(() => {
    if (!matrix) return [];
    return (matrix.students || [])
      .filter(s => risk.has(s.id))
      .map(s => ({ ...s, ...risk.get(s.id) }))
      .sort((a, b) => b.streak - a.streak || b.absent - a.absent || String(a.student_id).localeCompare(String(b.student_id)));
  }, [matrix, risk]);

  const nTodo = (todo.requests || 0) + (todo.pending || 0);
  const tasks = dups.length + (outsiders.length > 0 ? 1 : 0) + (nTodo > 0 ? 1 : 0);

  const openDrill = (kind, title) => setDrill(d => (d && d.kind === kind ? null : { kind, title }));
  const drillRows = !drill || !matrix ? []
    : drill.kind === 'placeholder'
      ? placeholders.map(s => ({ ...s, note: 'アプリ未登録' }))
      : rowsForOverview(matrix, drill.kind);

  const maxBar = 100;
  const recs = (s) => Number(s.attended_count || 0) + Number(s.unenrolled_count || 0);

  return (
    <div className="ws-overview">
      {enrolled === 0 && (
        <div className="ws-note ws-note-warn">
          <strong>履修者が登録されていないため、出席率を計算できません。</strong>
          出席率は「打刻数 ÷（実施回数 × 履修者数）」で出しています。分母の履修者が0人です。
          <Link to={'/students?classId=' + classId} style={{ marginLeft: '0.4rem' }}>学生管理で名簿を開く →</Link>
        </div>
      )}
      {enrolled > 0 && held === 0 && (
        <div className="ws-note">実施済みの回がまだないので、出席率は出せません（0% ではありません）。</div>
      )}

      <div className="ws-overview-grid">
        <div className="ws-col-main">
          <section className="ws-panel">
            <div className="ws-panel-head">
              <h2>回ごとの出席率</h2>
              <span className="ws-muted">棒を押すと、その回の詳細を「回ごと」で開きます</span>
            </div>
            {bars.length === 0 ? (
              <p className="ws-muted" style={{ margin: '1rem 0' }}>まだ実施した回がありません。</p>
            ) : (
              <>
                <div className="ws-bars" role="list">
                  {bars.map(b => (
                    <button key={b.s.id} type="button" role="listitem" className="ws-bar"
                            onClick={() => goTab('sessions', { session: b.s.id })}
                            title={`第${b.i + 1}回 ${fmtDate(b.s.session_date)} ${fmtClock(b.s.start_time)}　出席 ${b.att}人${b.rate != null ? '（' + b.rate + '%）' : ''}`}>
                      <span className="ws-bar-val">{b.rate == null ? '—' : Math.round(b.rate) + '%'}</span>
                      <span className="ws-bar-fill"
                            style={{ height: (b.rate == null ? 0 : Math.max(2, Math.min(b.rate, maxBar))) + '%', background: rateColor(b.rate) }} />
                    </button>
                  ))}
                </div>
                <div className="ws-bar-labels" aria-hidden="true">
                  {bars.map(b => <span key={b.s.id}>{fmtMD(b.s.session_date)}</span>)}
                </div>
                <div className="ws-legend">
                  <span><i style={{ background: rateColor(90) }} />80%以上</span>
                  <span><i style={{ background: rateColor(70) }} />60〜80%</span>
                  <span><i style={{ background: rateColor(40) }} />60%未満</span>
                </div>
              </>
            )}
          </section>

          <section className="ws-stat-row">
            <button type="button" className="ws-stat" onClick={() => openDrill('late', '遅刻した学生')}>
              <span className="ws-stat-label">遅刻（のべ）</span>
              <span className="ws-stat-num">{Number(ov.late_total || 0)}</span>
            </button>
            <button type="button" className="ws-stat" onClick={() => openDrill('early_leave', '早退した学生')}>
              <span className="ws-stat-label">早退（のべ）</span>
              <span className="ws-stat-num">{Number(ov.early_leave_total || 0)}</span>
            </button>
            <button type="button" className="ws-stat" onClick={() => openDrill('excused', '公欠の学生')}>
              <span className="ws-stat-label">公欠（のべ）</span>
              <span className="ws-stat-num">{matrix ? excusedTotal : '…'}</span>
            </button>
            <button type="button" className="ws-stat" onClick={() => openDrill('placeholder', 'アプリ未登録の学生')}>
              <span className="ws-stat-label">アプリ未登録</span>
              <span className="ws-stat-num">{matrix ? placeholders.length : '…'}<small>人</small></span>
            </button>
          </section>

          {drill && (
            <StudentList title={drill.title} rows={drillRows} loading={!matrix} classId={classId}
                         onClose={() => setDrill(null)} />
          )}
        </div>

        <div className="ws-col-side">
          <section className={'ws-panel' + (tasks > 0 ? ' ws-panel-warn' : '')}>
            <div className="ws-panel-head">
              <h2>{tasks > 0 ? `片付けが必要なこと（${tasks}件）` : '片付けが必要なこと'}</h2>
            </div>
            {tasks === 0 ? (
              <p className="ws-muted" style={{ margin: 0 }}>ありません。</p>
            ) : (
              <ul className="ws-task-list">
                {dups.map(([day, list]) => {
                  const keep = [...list].sort((a, b) => recs(b) - recs(a))[0];
                  return (
                    <li key={day}>
                      <span>
                        {fmtMD(day)} に回が{list.length}つあります
                        <span className="ws-muted">（{list.map(s => `${fmtClock(s.start_time) || '時刻なし'}・${recs(s)}人`).join(' / ')}）</span>
                      </span>
                      <button type="button" className="ws-link-btn"
                              onClick={() => navigate('/classes/' + classId + '/merge?keep=' + keep.id)}>まとめる →</button>
                    </li>
                  );
                })}
                {outsiders.length > 0 && (
                  <li>
                    <span>名簿にいない学生の打刻 {outsiders.length}人</span>
                    <button type="button" className="ws-link-btn" onClick={() => setShowOutsiders(v => !v)}>
                      {showOutsiders ? '閉じる' : '確かめる →'}
                    </button>
                  </li>
                )}
                {nTodo > 0 && (
                  <li>
                    <span>
                      {todo.requests > 0 && `学生からの申請 ${todo.requests}件`}
                      {todo.requests > 0 && todo.pending > 0 && '・'}
                      {todo.pending > 0 && `確認待ちの打刻 ${todo.pending}件`}
                    </span>
                    <Link className="ws-link-btn" to={'/classes/' + classId + '/edit'}>出欠の確認へ →</Link>
                  </li>
                )}
              </ul>
            )}
          </section>

          <section className="ws-panel">
            <div className="ws-panel-head">
              <h2>要注意の学生{matrix ? `（${riskList.length}人）` : ''}</h2>
              {riskList.length > 0 && (
                <button type="button" className="ws-link-btn" onClick={() => goTab('matrix', { filter: 'risk' })}>出欠表で見る</button>
              )}
            </div>
            {!matrix ? <p className="ws-muted" style={{ margin: 0 }}>読み込み中…</p>
              : riskList.length === 0 ? <p className="ws-muted" style={{ margin: 0 }}>いません。</p>
              : (
                <ul className="ws-risk-list">
                  {riskList.slice(0, 30).map(s => (
                    <li key={s.id}>
                      <button type="button" className="ws-risk-name" onClick={() => goTab('matrix', { student: s.id })}>
                        {s.name || '（氏名なし）'}
                        <span className="ws-muted">{s.student_id}</span>
                      </button>
                      <span className="ws-chip-risk">{s.reasons[0]}</span>
                    </li>
                  ))}
                  {riskList.length > 30 && (
                    <li className="ws-muted">ほか {riskList.length - 30}人（出欠表で「要注意だけ」を選ぶと全員見られます）</li>
                  )}
                </ul>
              )}
            <p className="ws-muted" style={{ fontSize: '0.78rem', margin: '0.6rem 0 0' }}>
              直近2回以上続けて欠席・累計3回以上欠席・出席率70%未満（実施4回以上）のどれか。書き出しの「要注意の学生」と同じ決め方です。
            </p>
          </section>
        </div>
      </div>

      {showOutsiders && outsiders.length > 0 && (
        <OutsiderPanel classId={classId} matrix={matrix} auth={auth} setErr={setErr} setMsg={setMsg}
                       onDone={onChanged} />
      )}
    </div>
  );
}
